import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { MockDataService } from "./mockData";

// Mock API function that simulates API responses using mock data
const mockApiResponse = async (url: string, params?: URLSearchParams): Promise<any> => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 100 + Math.random() * 200));

  const urlPath = url.split('?')[0]; // Remove query parameters
  
  // Dashboard stats
  if (urlPath === '/api/dashboard/stats') {
    return MockDataService.getDashboardStats();
  }
  
  // Purchase Orders
  if (urlPath === '/api/purchase-orders') {
    const page = parseInt(params?.get('page') || '1');
    const limit = parseInt(params?.get('limit') || '10');
    const search = params?.get('search') || '';
    const status = params?.get('status') || '';
    return MockDataService.getPurchaseOrders(page, limit, search, status);
  }
  
  if (urlPath === '/api/purchase-orders/pending-validation') {
    return MockDataService.getPendingValidationPOs();
  }
  
  // Individual PO
  if (urlPath.startsWith('/api/purchase-orders/') && !urlPath.includes('/items') && !urlPath.includes('/validation-issues')) {
    const id = parseInt(urlPath.split('/').pop() || '0');
    return MockDataService.getPurchaseOrderById(id);
  }
  
  // PO Items
  if (urlPath.includes('/api/purchase-orders/') && urlPath.endsWith('/items')) {
    const poId = parseInt(urlPath.split('/')[3]);
    return MockDataService.getPOItems(poId);
  }
  
  // Validation Issues
  if (urlPath.includes('/api/purchase-orders/') && urlPath.endsWith('/validation-issues')) {
    const poId = parseInt(urlPath.split('/')[3]);
    return MockDataService.getValidationIssues(poId);
  }
  
  // Inventory
  if (urlPath === '/api/inventory') {
    const page = parseInt(params?.get('page') || '1');
    const limit = parseInt(params?.get('limit') || '10');
    const search = params?.get('search') || '';
    const status = params?.get('status') || '';
    return MockDataService.getInventoryItems(page, limit, search, status);
  }
  
  if (urlPath === '/api/inventory/alerts') {
    return MockDataService.getLowStockItems();
  }
  
  // Audit Logs
  if (urlPath === '/api/audit-logs') {
    const page = parseInt(params?.get('page') || '1');
    const limit = parseInt(params?.get('limit') || '10');
    const userId = params?.get('userId') ? parseInt(params.get('userId')!) : undefined;
    const teamName = params?.get('teamName') || undefined;
    return MockDataService.getAuditLogs(page, limit, userId, teamName);
  }
  
  if (urlPath === '/api/audit-logs/recent') {
    const limit = parseInt(params?.get('limit') || '10');
    return MockDataService.getRecentAuditLogs(limit);
  }
  
  if (urlPath.startsWith('/api/audit-logs/team/')) {
    const teamName = urlPath.split('/').pop()!;
    return MockDataService.getTeamAuditLogs(teamName);
  }
  
  if (urlPath.startsWith('/api/audit-logs/user/')) {
    const userId = parseInt(urlPath.split('/').pop()!);
    return MockDataService.getAuditLogs(1, 50, userId);
  }
  
  // Reports
  if (urlPath === '/api/reports') {
    const timeRange = params?.get('timeRange') || '30d';
    const category = params?.get('category') || undefined;
    const brand = params?.get('brand') || undefined;
    return MockDataService.getReports(timeRange, category, brand);
  }
  
  // Default empty response
  return {};
};

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Simulate API request with mock data
  const params = new URLSearchParams(url.split('?')[1] || '');
  const mockResponse = await mockApiResponse(url, params);
  
  // Create a mock Response object
  return new Response(JSON.stringify(mockResponse), {
    status: 200,
    headers: { 'Content-Type': 'application/json' }
  });
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey[0] as string;
    const params = new URLSearchParams(url.split('?')[1] || '');
    return await mockApiResponse(url, params);
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
